/* oxlint-disable typescript/no-unnecessary-condition -- LINTER migration follow up */
import type { ZodError } from "zod";

export type LogLevel = "info" | "warn" | "error";

export interface AppErrorOptions {
  httpCode?: number;
  logLevel?: LogLevel;
  cause?: unknown;
  code?: unknown;
  context?: Record<string, unknown>;
}

export abstract class AppError extends Error {
  public readonly httpCode: number;
  public logLevel: LogLevel;
  public readonly code?: unknown;
  public readonly context?: Record<string, unknown>;

  constructor(message: string, options?: AppErrorOptions) {
    super(message, { cause: options?.cause }); // Pass cause to native Error
    this.name = this.constructor.name;
    this.httpCode = options?.httpCode ?? 500;
    this.logLevel = options?.logLevel ?? "error";
    this.code = options?.code;
    this.context = options?.context ?? {};

    // Restore the prototype chain
    Object.setPrototypeOf(this, new.target.prototype);

    // Optional: capture stack trace (especially in Node)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }
}

export class NotFoundError extends AppError {
  constructor(message: string, options?: AppErrorOptions) {
    super(message, { logLevel: "error", httpCode: 404, ...options });
    this.name = "NotFoundError";
  }
}

export class ForbiddenError extends AppError {
  constructor(message: string, options?: AppErrorOptions) {
    super(message, { logLevel: "warn", httpCode: 403, ...options });
    this.name = "ForbiddenError";
  }
}

export class UnauthorizedError extends AppError {
  constructor(message = "Unauthorized", options?: AppErrorOptions) {
    super(message, { logLevel: "warn", httpCode: 401, ...options });
    this.name = "UnauthorizedError";
  }
}

export class RepositoryError extends AppError {
  constructor(message: string, options?: AppErrorOptions) {
    super(message, { logLevel: "error", ...options });
    this.name = "RepositoryError";
  }
}

export class EntityNotFoundError extends RepositoryError {
  constructor({
    entityName,
    key,
    valueAsString,
    options,
  }: { entityName: string; key: string | string[]; valueAsString: string | string[]; options?: AppErrorOptions }) {
    super(
      `${entityName} with key ${Array.isArray(key) ? key.join(",") : key} and value ${Array.isArray(valueAsString) ? valueAsString.join(",") : valueAsString} not found`,
      {
        httpCode: 404,
        logLevel: "error",
        ...options,
      },
    );
    this.name = "EntityNotFoundError";
  }
}

export class BadRequestError extends AppError {
  constructor(message: string, options?: AppErrorOptions) {
    super(message, { ...options, httpCode: 400, logLevel: "warn" });
    this.name = "BadRequestError";
  }
}

export class ConflictError extends RepositoryError {
  constructor(message: string, options?: AppErrorOptions) {
    super(message, { ...options, httpCode: 409 });
    this.name = "ConflictError";
  }
}

export class ConnectionError extends RepositoryError {
  errorCode?: string;
  constructor(message = "Database connection error", options?: AppErrorOptions & { errorCode?: string }) {
    super(message, options);
    this.name = "ConnectionError";
    this.errorCode = options?.errorCode;
  }
}

export class UnexpectedError extends AppError {
  constructor(message = "An unknown error occurred", options?: AppErrorOptions) {
    super(message, { ...options, logLevel: "error" });
    this.name = "UnknownError";
  }
}

export class StaleAssetError extends AppError {
  constructor(message = "Stale asset detected", options?: AppErrorOptions) {
    super(message, { ...options, logLevel: "warn" });
    this.name = "StaleAssetError";
  }
}

export class InputError extends AppError {
  constructor({
    message = "Unexpected input ",
    options,
    cause,
  }: { message?: string; options?: AppErrorOptions; cause?: ZodError } = {}) {
    super(message, { ...options, httpCode: 400, logLevel: "warn", cause });
    this.name = "InputError";
  }
}

export const VERSION_READ_ONLY_ERROR_CODE = "VERSION_READ_ONLY";

/** The tab booted with another session (re-login in another tab): its in-memory organisation state is stale. */
export const SESSION_CHANGED_ERROR_CODE = "SESSION_CHANGED";

export function assertUnreachable(_x: never): never {
  throw new UnexpectedError("Didn't expect to get here");
}
